import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Copy,
  CheckCircle2,
  ArrowRight,
  Share2,
  Sparkles,
  AlertTriangle,
  Mail,
} from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AffiliateWelcome
 *
 * Landing page the affiliate is redirected to after clicking the email
 * verification link. Shows the freshly-issued referral code, a copy
 * button, and the exact same "how to share it" instructions they'll have
 * received in the welcome email.
 *
 * If the code in the URL doesn't validate against the backend we show a
 * friendly error state instead of a broken-looking page.
 */
const AffiliateWelcome = () => {
  const location = useLocation();
  const code = (new URLSearchParams(location.search).get('code') || '').toUpperCase();
  const [state, setState] = useState('loading'); // loading | valid | invalid
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!code) {
      setState('invalid');
      return;
    }
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/affiliate/validate/${encodeURIComponent(code)}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setState(data.valid ? 'valid' : 'invalid');
      })
      .catch(() => !cancelled && setState('invalid'));
    return () => {
      cancelled = true;
    };
  }, [code]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* no-op */
    }
  };

  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO */}
      <section className="relative pt-[110px] md:pt-[170px] pb-16 md:pb-28 px-6 overflow-hidden min-h-[580px] md:min-h-[760px]">
        <AltParallaxBackdrop />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7">
            <Sparkles className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.5} />
            You're in
          </span>
          <h1 className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow">
            Welcome,{' '}
            <em className="italic font-light text-blue-600">partner.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            Your affiliate account is live. Here's your unique code — keep it
            somewhere safe, you'll use it everywhere.
          </p>
        </div>
      </section>

      {/* CODE + INSTRUCTIONS */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto">
          {state === 'loading' && (
            <div
              data-testid="affiliate-welcome-loading"
              className="rounded-3xl bg-[#f4f8fc] ring-1 ring-black/5 p-10 text-center"
            >
              <p className="text-[#0b1f3a]/60 text-sm">
                Loading your referral code…
              </p>
            </div>
          )}

          {state === 'invalid' && (
            <div
              data-testid="affiliate-welcome-invalid"
              className="rounded-3xl bg-amber-50 ring-1 ring-amber-200 p-10 text-center"
            >
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
              <h3 className="text-2xl font-black text-[#0b1f3a] mb-2">
                We couldn't find this code.
              </h3>
              <p className="text-[#0b1f3a]/70 max-w-md mx-auto leading-relaxed">
                The link may have been mistyped, or this affiliate account has
                been deactivated. If you believe this is a mistake, write to
                us at{' '}
                <a
                  href="mailto:business@levanta.life"
                  className="text-blue-600 underline"
                >
                  business@levanta.life
                </a>
                .
              </p>
              <Link
                to="/affiliate"
                className="inline-flex items-center gap-3 mt-8 px-7 py-3.5 rounded-full bg-[#0b1f3a] text-white font-semibold hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
              >
                Back to the program
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {state === 'valid' && (
            <>
              {/* CODE CARD */}
              <div
                data-testid="affiliate-welcome-card"
                className="rounded-3xl bg-gradient-to-br from-[#eaf3fb] via-white to-[#f7fbff] ring-1 ring-blue-100 shadow-xl p-10 md:p-14 text-center relative overflow-hidden"
              >
                <div className="absolute -top-20 -right-20 w-60 h-60 bg-blue-200/40 rounded-full blur-3xl" />
                <div className="relative">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4">
                    Your referral code
                  </p>
                  <p
                    data-testid="affiliate-welcome-code"
                    className="font-mono text-4xl md:text-7xl font-black text-[#0b1f3a] tracking-[0.12em] mb-2 break-all"
                  >
                    {code}
                  </p>
                  <p className="text-[#0b1f3a]/55 text-sm mb-8">
                    8 characters · case-insensitive · unique to you
                  </p>
                  <button
                    type="button"
                    onClick={copyCode}
                    data-testid="affiliate-welcome-copy"
                    className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#0b1f3a] text-white font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
                  >
                    {copied ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy code
                      </>
                    )}
                  </button>

                  {/* Live affiliate dashboard — sign in via the email magic
                      link, then track clicks / referrals / earnings. */}
                  <div className="mt-6">
                    <Link
                      to="/affiliates/login"
                      data-testid="affiliate-welcome-dashboard"
                      className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:underline"
                    >
                      Open your live affiliate dashboard
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* HOW TO USE IT */}
              <div className="mt-16">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
                  How to use it
                </p>
                <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-12">
                  Three steps.{' '}
                  <em className="italic font-light text-blue-600">
                    Then it pays forever.
                  </em>
                </h2>

                <div className="grid md:grid-cols-3 gap-5">
                  {[
                    {
                      n: '01',
                      Icon: Share2,
                      t: 'Share the code',
                      b: 'Drop it in videos, captions, stories, podcast descriptions, DMs or a verbal mention. Wherever your audience is.',
                    },
                    {
                      n: '02',
                      Icon: Sparkles,
                      t: 'Your followers enter it',
                      b: `At checkout in the Levanta app, they type ${code}. They instantly get 10% off their membership.`,
                    },
                    {
                      n: '03',
                      Icon: CheckCircle2,
                      t: 'You earn — every month',
                      b: '40% of their first month. 20% every month they stay. Your dashboard tracks it all in real time.',
                    },
                  ].map(({ n, Icon, t, b }) => (
                    <div
                      key={n}
                      className="rounded-3xl bg-[#f4f8fc] ring-1 ring-black/5 p-7 hover:bg-white hover:shadow-lg transition-all duration-300"
                    >
                      <p className="font-mono text-[11px] tracking-[0.22em] text-blue-600/70 mb-4">
                        STEP {n}
                      </p>
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 text-white flex items-center justify-center mb-4 shadow-md">
                        <Icon className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <h3 className="text-lg font-black text-[#0b1f3a] mb-2">
                        {t}
                      </h3>
                      <p className="text-[#0b1f3a]/70 leading-relaxed text-[15px]">
                        {b}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-10 rounded-3xl bg-[#0b1f3a] text-white p-8 md:p-10">
                  <div className="flex items-start gap-4">
                    <Mail className="w-6 h-6 text-cyan-300 shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-black mb-2">
                        Questions? Talk to us.
                      </h3>
                      <p className="text-white/75 leading-relaxed">
                        Write to{' '}
                        <a
                          href="mailto:business@levanta.life"
                          className="text-blue-300 underline hover:text-white"
                        >
                          business@levanta.life
                        </a>{' '}
                        anytime — we answer within one business day. A copy of
                        this page has also been sent to your email for safe
                        keeping.
                      </p>
                      <p className="mt-4 text-sm text-white/55 leading-relaxed">
                        Reminder: disclose the partnership in every piece of
                        content you post (use #ad / #partner / #affiliate). Read
                        the full{' '}
                        <Link
                          to="/affiliate/terms"
                          className="underline hover:text-white"
                        >
                          Affiliate Program terms
                        </Link>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default AffiliateWelcome;
